import { useEffect, useMemo, useState } from "react";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Popover } from "@remo-co/ui-core/src/components/Popover";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { ChevronRight } from "@remo-co/ui-core/src/icons/ChevronRight";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { Edit } from "@remo-co/ui-core/src/icons/Edit";
import { useI18n } from "i18n";
import times from "lodash/times";
import { useJoinFloor } from "modules/joinFloor";
import { useSelector } from "react-redux";
import {
  selectCurrentTheaterId,
  selectTheaterSpaces,
} from "modules/theater/selectors";
import { selectCurrentSpaceId } from "modules/space/redux/selectors";
import { selectUserForceMoveData } from "modules/userForceMove/redux/selectors";
import { selectUserId } from "modules/auth/redux/selectors";
import { usePrevious } from "helpers/reactHooksUtils";
import { DNS_KEYS } from "modules/globalUserPreference";
import { useFloorNames } from "modules/floors/hooks/useFloorNames";
import { selectUsersPerSpace } from "modules/eventUserPresence/redux/selectors";
import { EditFloorNamesForm } from "modules/floors/components/EditFloorNamesForm";
import {
  selectCurrentEventId,
  selectIsEventManager,
} from "modules/event/selectors";
import { selectCurrentUserPreferences } from "modules/userPreferences/redux/selectors";
import {
  addDialogNotification,
  closeDialogNotification,
} from "modules/dialogNotification/redux/dialogNotificationSlice";
import { useAppDispatch } from "store/hooks";
import { useStyles } from "./styles";

const { disableSwitchFloorNotification } = DNS_KEYS;

interface Props {
  showMenu: boolean;
  anchorRef: React.RefObject<HTMLElement>;
  setShowMenu: (value: boolean) => void;
}

export const FloorSwitchPopover = ({
  showMenu,
  setShowMenu,
  anchorRef,
}: Props) => {
  const styles = useStyles();
  const { t } = useI18n(["common", "event"]);
  const { switchFloor } = useJoinFloor();
  const spaces = useSelector(selectTheaterSpaces);
  const currentSpaceId = useSelector(selectCurrentSpaceId);
  const usersPerSpace = useSelector(selectUsersPerSpace);
  const userForceMoveData = useSelector(selectUserForceMoveData);
  const currentEventId = useSelector(selectCurrentEventId);
  const currentUserId = useSelector(selectUserId);
  const currentTheaterId = useSelector(selectCurrentTheaterId);
  const floorNames = useFloorNames(currentTheaterId);
  const hasEventManagerAccess = useSelector(selectIsEventManager);
  const [showEditFloorNamesForm, setShowEditFloorNamesForm] = useState(false);
  const currentUserPreferences = useSelector(selectCurrentUserPreferences);
  const dispatch = useAppDispatch();

  const currentUserIsBeingMoved =
    userForceMoveData && userForceMoveData.userId === currentUserId;
  const prevCurrentUserIsBeingMoved = usePrevious(currentUserIsBeingMoved);

  const userCountInSpaces = useMemo(
    () =>
      Object.entries(usersPerSpace).reduce((acc, [spaceId, users]) => {
        acc[spaceId] = users.length;
        return acc;
      }, {} as Record<string, number>),
    [usersPerSpace],
  );

  useEffect(() => {
    if (currentUserIsBeingMoved && !prevCurrentUserIsBeingMoved) {
      setShowMenu(false);
    }
  }, [currentUserIsBeingMoved, prevCurrentUserIsBeingMoved, setShowMenu]);

  if (spaces.length <= 1) {
    return null;
  }

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const isDNSDisabled = () =>
    !(
      currentUserPreferences &&
      currentUserPreferences[disableSwitchFloorNotification]
    );

  const onFloorChangeConfirm = (selectedSpace: string) => {
    if (selectedSpace) {
      switchFloor({ targetFloorId: selectedSpace });
    }
  };

  const beforeFloorChange = (selectedSpace: string) => {
    setShowMenu(false);
    if (selectedSpace !== currentSpaceId) {
      // If do not show notification is enabled, dont show notification again

      if (isDNSDisabled()) {
        dispatch(closeDialogNotification());
        dispatch(
          addDialogNotification({
            title: t("dialog.move.floors.title"),
            message: t("dialog.message.elevator"),
            confirmText: t("dialog.move.floors.confirm"),
            dismissText: t("dialog.cancel.elevator"),
            onConfirm: () => onFloorChangeConfirm(selectedSpace),
            hasDoNotShowAgain: true,
            isDoNotShowGlobal: true,
            dnsKey: disableSwitchFloorNotification,
          }),
        );
      } else {
        onFloorChangeConfirm(selectedSpace);
      }
    }
  };

  return (
    <Popover
      anchorEl={anchorRef.current}
      anchorOrigin={{
        horizontal: "center",
        vertical: "bottom",
      }}
      transformOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      open={showMenu}
      onClose={toggleMenu}
      classes={{ paper: styles.menuPopover }}
    >
      <Container
        flex
        justifyContent="space-between"
        className={styles.headerContainer}
      >
        <Typography variant="h3">{t("event:top.bar.event.floors")}</Typography>
        <Container fullWidth={false} className={styles.buttons}>
          {hasEventManagerAccess && (
            <Edit
              onClick={() => setShowEditFloorNamesForm(true)}
              className={styles.button}
              data-testid="edit-floor-names-button"
            />
          )}
          <Close onClick={toggleMenu} className={styles.button} />
        </Container>
      </Container>
      <Container fullWidth className={styles.container}>
        {times(spaces.length, (i: number) => {
          const floor = spaces.length - i;
          const space = spaces[floor - 1];
          const isCurrent = space === currentSpaceId;
          const guestCount = space ? userCountInSpaces[space] || 0 : 0;

          return (
            <Container
              flex
              key={floor}
              justifyContent="space-between"
              padding="xs"
              data-dd-action-name="Select Floor"
              className={styles.floorContainer}
            >
              <Container
                flex
                justifyContent="flex-start"
                alignItems="center"
                className={styles.switchFloorButton}
                onClick={() => space && beforeFloorChange(space)}
              >
                <Typography variant="body1" className={styles.floorNumber}>
                  {floor}
                </Typography>
                <Container>
                  <Typography variant="body1" className={styles.floorName}>
                    {space && floorNames[space]
                      ? floorNames[space]
                      : t("event:top.bar.floor", { floor })}
                  </Typography>
                  <Typography
                    variant="body1"
                    className={styles.guestCount}
                    data-testid={`${space}—${guestCount}`}
                  >
                    {t("event:guest.count", { guestCount })}
                  </Typography>
                </Container>
              </Container>
              {isCurrent ? (
                <Typography variant="body1" className={styles.youAreHere}>
                  {t("event:top.bar.you.are.here")}
                </Typography>
              ) : (
                <Button
                  variant="text"
                  type="submit"
                  className={styles.switchButton}
                  onClick={() => space && beforeFloorChange(space)}
                >
                  <ChevronRight />
                </Button>
              )}
            </Container>
          );
        })}
      </Container>
      {showEditFloorNamesForm && currentEventId && (
        <EditFloorNamesForm
          eventId={currentEventId}
          theaterId={currentTheaterId}
          spaces={spaces}
          close={() => {
            setShowEditFloorNamesForm(false);
            toggleMenu();
          }}
        />
      )}
    </Popover>
  );
};
